import * as THREE from 'three';
import { Quat } from '@/core/math/Quat';
import { Vec3 } from '@/core/math/Vec3';
import { ChunkRecord } from '@/planet/PlanetSurface';
import { SURFACE_COMPONENTS } from '@/planet/ChunkBuilder';
import { CelestialBody } from '@/universe/CelestialBody';
import { createAtmosphereMaterial, AtmosphereUniformSet } from './shaders/AtmosphereMaterial';
import { createChunkMaterial, createSharedTerrainUniforms, SharedTerrainUniforms } from './shaders/TerrainMaterial';
import { createSkyMaterial, SkyUniformSet } from './shaders/SkyMaterial';
import { setShadowCascadeUniforms, setShadowFadeUniform } from './shaders/shadowCascade';
import { RENDER_LAYER, ShadowCascade } from './LayeredRenderer';

/**
 * Frees an attribute's CPU-side array as soon as the GPU has a copy.
 *
 * Terrain geometry is the largest thing this project keeps in memory by an
 * order of magnitude: 312 KB per chunk at `resolution: 64`, against a
 * `maxChunks` budget of 1200. Measured on Halcyon standing on the ground,
 * 731 resident chunks held 222.8 MB of live typed arrays — and every byte of
 * that was *also* on the GPU, because `BufferAttribute` keeps `array` after
 * upload so it can re-upload on demand. Nothing here ever re-uploads: a chunk
 * is built once, drawn until it is evicted, and rebuilt from the seed if it is
 * ever wanted again.
 *
 * `onUploadCallback` runs inside Three's own upload path with `this` bound to
 * the attribute, immediately after `bufferData`. Dropping the array there is
 * the documented way to do this; the attribute keeps its `count` and
 * `itemSize`, so everything the draw call needs survives.
 *
 * The counterpart is `releaseChunkMeshArrays` on the streaming side, which
 * drops the *other* reference to the same arrays. Either one alone frees
 * nothing.
 */
function releaseAfterUpload(attribute: THREE.BufferAttribute): void {
  attribute.onUpload(function (this: THREE.BufferAttribute) {
    // Not `null`: Three reads `array.length`/`BYTES_PER_ELEMENT` in places
    // that run after upload, and a typed array of length zero satisfies them
    // while holding no storage.
    this.array = new (this.array.constructor as Float32ArrayConstructor)(0);
  });
}

/**
 * Renders one body's streamed terrain, plus its atmosphere shell.
 *
 * Coordinate handling is the whole point of this class:
 *
 *   - Chunk geometry is authored relative to its own centre, so vertex
 *     coordinates never exceed the chunk's own size. A 32-bit float has ~7
 *     significant digits, which is plenty for a few hundred metres but not for
 *     a position six million metres from a planet's core.
 *
 *   - Each frame, a chunk's mesh position is set to
 *     `chunkCentreFixed - cameraPositionFixed`, computed in 64-bit and then
 *     narrowed. Near the ground that value is small, so precision is excellent
 *     exactly where it is needed; from orbit it is large, but a metre of error
 *     at six thousand kilometres is invisible.
 *
 *   - All chunks hang off a group carrying the planet-fixed to render-frame
 *     rotation, so planetary rotation costs one quaternion rather than a
 *     rebuild.
 *
 * The atmosphere shell lives in the inertial frame (it does not rotate with the
 * surface for rendering purposes) and is drawn after the terrain with depth
 * writes disabled.
 */
export class TerrainRenderer {
  readonly root = new THREE.Group();
  readonly surfaceGroup = new THREE.Group();
  readonly sharedUniforms: SharedTerrainUniforms;

  private meshes = new Map<string, THREE.Mesh>();
  /** GPU bytes per chunk geometry, recorded at upload time — see `addChunk`. */
  private geometryBytes = new Map<string, number>();
  private atmosphereMesh: THREE.Mesh | null = null;
  private atmosphereUniforms: AtmosphereUniformSet | null = null;
  private skyMesh: THREE.Mesh | null = null;
  private skyUniforms: SkyUniformSet | null = null;
  private readonly scratch = new Vec3();
  private wireframe = false;

  constructor(
    readonly body: CelestialBody,
    seaLevelRadius: number,
    atmosphereSteps: number,
    atmosphereLightSteps: number,
  ) {
    this.sharedUniforms = createSharedTerrainUniforms(body, seaLevelRadius);
    this.root.frustumCulled = false;
    this.surfaceGroup.frustumCulled = false;
    this.root.add(this.surfaceGroup);

    if (body.hasAtmosphere) {
      const radius = body.radius + body.atmosphereTop;
      if (atmosphereSteps > 0) {
        const { material, uniforms } = createAtmosphereMaterial(body, atmosphereSteps, atmosphereLightSteps);
        // 64 segments put a visible polygon on the silhouette: at 64 the shell's
        // limb is a chain of straight chords a couple of degrees long each, and
        // against black space that reads as the atmosphere having a hard
        // straight edge. The shell is one mesh per body and drawn in a single
        // pass, so the extra triangles are irrelevant next to the ray march
        // running in its fragment shader.
        const geometry = new THREE.SphereGeometry(radius, 256, 128);
        this.atmosphereMesh = new THREE.Mesh(geometry, material);
        this.atmosphereMesh.frustumCulled = false;
        // Drawn after opaque terrain so it composites over it, and only in the
        // far pass: a shell that surrounds the camera is inside every
        // partition's frustum, so without a layer this ray march would run once
        // per world pass over the whole screen.
        this.atmosphereMesh.renderOrder = 100;
        this.atmosphereMesh.layers.set(RENDER_LAYER.sky);
        this.atmosphereUniforms = uniforms;
        this.root.add(this.atmosphereMesh);
      } else {
        // Ray marching is disabled by the quality profile. Fall back to the
        // analytic sky rather than to nothing: a black daytime sky is a wrong
        // picture, not a cheaper one.
        const { material, uniforms } = createSkyMaterial(body);
        const geometry = new THREE.SphereGeometry(radius, 32, 16);
        this.skyMesh = new THREE.Mesh(geometry, material);
        this.skyMesh.frustumCulled = false;
        this.skyMesh.renderOrder = 100;
        this.skyMesh.layers.set(RENDER_LAYER.sky);
        this.skyUniforms = uniforms;
        this.root.add(this.skyMesh);
      }
    }
  }

  /** Adds or replaces a chunk's mesh. */
  addChunk(record: ChunkRecord): void {
    const data = record.data;
    if (!data) return;

    this.removeChunk(record.key);

    let bytes = 0;
    const geometry = new THREE.BufferGeometry();
    const attach = (name: string | null, array: THREE.TypedArray, itemSize: number): void => {
      const attribute = new THREE.BufferAttribute(array, itemSize);
      bytes += array.byteLength;
      releaseAfterUpload(attribute);
      if (name === null) geometry.setIndex(attribute);
      else geometry.setAttribute(name, attribute);
    };
    attach('position', data.positions, 3);
    attach('normal', data.normals, 3);
    attach('color', data.colors, 3);
    attach('aSurface', data.surface, SURFACE_COMPONENTS);
    attach(null, data.indices, 1);
    // Recorded now because `releaseAfterUpload` drops the arrays these came
    // from — `estimateGpuBytes` cannot measure them once the GPU owns them.
    // The number it wants is the GPU-side size anyway, which does not change.
    this.geometryBytes.set(record.key, bytes);
    // Supplying the bounding sphere directly avoids a full pass over the
    // vertices, and it is exactly what the frustum culler needs to decide which
    // depth partition should draw this chunk. It is also now load-bearing
    // rather than merely an optimisation: with the vertex data released, Three
    // could not compute one on demand if it were missing.
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), data.boundingRadius);

    const origin = new THREE.Vector3(data.centre[0], data.centre[1], data.centre[2]);
    const material = createChunkMaterial(this.sharedUniforms, origin, this.wireframe);

    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = `chunk:${record.key}`;
    mesh.matrixAutoUpdate = true;
    mesh.userData.centre = data.centre;
    mesh.userData.level = data.level;
    // In addition to the default layer, so the self-shadow cascade cameras
    // (which enable only this layer) can select "terrain chunks" without a
    // parallel scene graph — see RENDER_LAYER.terrainShadowCaster.
    mesh.layers.enable(RENDER_LAYER.terrainShadowCaster);
    this.meshes.set(record.key, mesh);
    this.surfaceGroup.add(mesh);
  }

  removeChunk(key: string): void {
    const mesh = this.meshes.get(key);
    if (!mesh) return;
    this.surfaceGroup.remove(mesh);
    mesh.geometry.dispose();
    (mesh.material as THREE.Material).dispose();
    this.meshes.delete(key);
    this.geometryBytes.delete(key);
  }

  /** Shows only the chunks the LOD tree currently considers visible. */
  setVisible(keys: ReadonlySet<string>): void {
    for (const [key, mesh] of this.meshes) {
      mesh.visible = keys.has(key);
    }
  }

  /**
   * Positions everything for this frame.
   *
   * Render positions are camera-relative but stay in *inertial* axes: the
   * camera carries the view rotation and Three applies it once. Pre-rotating
   * here as well would apply it twice.
   *
   * @param cameraPositionFixed camera position in the body-fixed frame, metres
   * @param fixedToInertial rotation from body-fixed axes into inertial axes
   * @param planetCentreRender planet centre relative to the camera, metres
   */
  update(cameraPositionFixed: Vec3, fixedToInertial: Quat, planetCentreRender: Vec3): void {
    this.surfaceGroup.quaternion.set(
      fixedToInertial.x,
      fixedToInertial.y,
      fixedToInertial.z,
      fixedToInertial.w,
    );
    this.surfaceGroup.position.set(0, 0, 0);

    for (const mesh of this.meshes.values()) {
      if (!mesh.visible) continue;
      const c = mesh.userData.centre as [number, number, number];
      // 64-bit subtraction, then narrowed to float32 for the GPU.
      this.scratch.set(c[0] - cameraPositionFixed.x, c[1] - cameraPositionFixed.y, c[2] - cameraPositionFixed.z);
      mesh.position.set(this.scratch.x, this.scratch.y, this.scratch.z);
    }

    this.sharedUniforms.uPlanetCentre.value.set(planetCentreRender.x, planetCentreRender.y, planetCentreRender.z);

    if (this.atmosphereMesh && this.atmosphereUniforms) {
      this.atmosphereMesh.position.set(planetCentreRender.x, planetCentreRender.y, planetCentreRender.z);
      this.atmosphereUniforms.uPlanetCentre.value.copy(this.atmosphereMesh.position);
    }
    if (this.skyMesh && this.skyUniforms) {
      this.skyMesh.position.set(planetCentreRender.x, planetCentreRender.y, planetCentreRender.z);
      this.skyUniforms.uPlanetCentre.value.copy(this.skyMesh.position);
    }
  }

  /**
   * Updates surface, atmosphere, and sky lighting.
   *
   * All three take the same un-gated `intensity`. This used to accept a
   * separately horizon-gated `surfaceIntensity` for the ground, on the
   * reasoning that the ground cannot see a star through its own planet while
   * the high atmosphere and sunset sky still can — true, but a per-body
   * uniform is the wrong place to express it, because the answer is different
   * on every part of the body at once. The terrain shader evaluates that ramp
   * per fragment instead; see `SURFACE_DAYLIGHT_START` in TerrainMaterial.
   */
  setLighting(
    sunDirection: Vec3,
    sunColor: THREE.Color,
    intensity: number,
    cameraAltitude: number,
  ): void {
    const u = this.sharedUniforms;
    u.uSunDirection.value.set(sunDirection.x, sunDirection.y, sunDirection.z);
    u.uSunColor.value.copy(sunColor);
    u.uSunIntensity.value = intensity;
    u.uCameraAltitude.value = cameraAltitude;

    if (this.atmosphereUniforms) {
      this.atmosphereUniforms.uSunDirection.value.set(sunDirection.x, sunDirection.y, sunDirection.z);
      this.atmosphereUniforms.uSunColor.value.copy(sunColor);
      this.atmosphereUniforms.uSunIntensity.value = intensity;
    }
    if (this.skyUniforms) {
      this.skyUniforms.uSunDirection.value.set(sunDirection.x, sunDirection.y, sunDirection.z);
      this.skyUniforms.uSunColor.value.copy(sunColor);
      this.skyUniforms.uSunIntensity.value = intensity;
    }
  }

  /**
   * Ambient light is derived from the atmosphere, not authored. A thick
   * atmosphere scatters a lot of skylight; a vacuum scatters none, so shadows on
   * an airless moon are genuinely black except for planetshine.
   */
  setAmbient(sky: THREE.Color, ground: THREE.Color): void {
    this.sharedUniforms.uAmbientSky.value.copy(sky);
    this.sharedUniforms.uAmbientGround.value.copy(ground);
  }

  /**
   * Light reflected from neighbouring bodies. Unlike `setAmbient` this does not
   * scale with atmospheric density, so it is the term that survives on an
   * airless world.
   */
  setPlanetshine(direction: Vec3, color: THREE.Color, intensity: number): void {
    const u = this.sharedUniforms;
    u.uPlanetshineDir.value.set(direction.x, direction.y, direction.z);
    u.uPlanetshineColor.value.copy(color);
    u.uPlanetshine.value = intensity;
  }

  /**
   * Points the cloud shadow at the deck that casts it.
   *
   * `sunDirectionFixed` must be in PLANET-FIXED axes, not the inertial axes the
   * rest of the lighting uses: the density field is defined over planet-fixed
   * directions, because weather sits above the ground rather than above a point
   * in space. Passing the inertial direction instead would leave the shadows
   * sliding across the surface as the planet turned.
   *
   * Pass a deck radius of 0 on a body with no cloud layer; the shader skips the
   * whole lookup on that test.
   */
  setCloudShadow(
    sunDirectionFixed: Vec3,
    deckRadius: number,
    coverage: number,
    drift: number,
    banded: number,
    time: number,
  ): void {
    const u = this.sharedUniforms;
    u.uSunDirectionFixed.value.set(sunDirectionFixed.x, sunDirectionFixed.y, sunDirectionFixed.z);
    u.uCloudDeckRadius.value = deckRadius;
    u.uCloudCoverage.value = coverage;
    u.uCloudDrift.value = drift;
    u.uCloudBanded.value = banded;
    u.uTime.value = time;
  }

  /**
   * Points the eclipse shadow at whichever nearby body currently occludes
   * the star, in the same PLANET-FIXED axes `setCloudShadow` uses (relative
   * to this body's own centre). Pass a radius of 0 when nothing is eclipsing
   * this body; the shader skips the lookup on that test.
   */
  setEclipseShadow(occluderCentreFixed: Vec3, occluderRadius: number): void {
    const u = this.sharedUniforms;
    u.uEclipseCentre.value.set(occluderCentreFixed.x, occluderCentreFixed.y, occluderCentreFixed.z);
    u.uEclipseRadius.value = occluderRadius;
  }

  /** See `setShadowCascadeUniforms` — the lookup is shared with the ocean surface. */
  setSelfShadow(cascades: readonly ShadowCascade[]): void {
    setShadowCascadeUniforms(this.sharedUniforms, cascades);
  }

  /** See LayeredRenderer.adaptShadowDistance for why this exists as a separate, continuous fade. */
  setShadowFade(fade: number): void {
    setShadowFadeUniform(this.sharedUniforms, fade);
  }

  /**
   * Cross-fade this body against its own distant impostor: 1 is fully real
   * terrain, 0 is fully impostor. Drives the surface (a stochastic discard —
   * see GLSL_DITHER) and its atmosphere and sky shells (a straight fade on
   * radiance) together, because a shell that popped in over dithering ground
   * would be just as visible a transition as the one this replaces.
   */
  setSurfaceFade(fade: number): void {
    this.sharedUniforms.uSurfaceFade.value = fade;
    if (this.atmosphereUniforms) this.atmosphereUniforms.uLayerFade.value = fade;
    if (this.skyUniforms) this.skyUniforms.uLayerFade.value = fade;
  }

  /**
   * Shows or hides the atmosphere shell, the sky shell and the terrain surface
   * independently. Debug support: several problems this session were only
   * separable by taking one layer away and seeing what changed — the question
   * "is the atmosphere darkening the terrain" has no answer from a screenshot.
   */
  setLayerVisible(layer: 'surface' | 'atmosphere' | 'sky', visible: boolean): void {
    if (layer === 'surface') this.surfaceGroup.visible = visible;
    if (layer === 'atmosphere' && this.atmosphereMesh) this.atmosphereMesh.visible = visible;
    if (layer === 'sky' && this.skyMesh) this.skyMesh.visible = visible;
  }

  setWireframe(enabled: boolean): void {
    this.wireframe = enabled;
    for (const mesh of this.meshes.values()) {
      (mesh.material as THREE.ShaderMaterial).wireframe = enabled;
    }
  }

  get chunkCount(): number {
    return this.meshes.size;
  }

  get visibleChunkCount(): number {
    let n = 0;
    for (const m of this.meshes.values()) if (m.visible) n++;
    return n;
  }

  /**
   * GPU memory held by terrain geometry, bytes.
   *
   * Summed from the sizes recorded in `addChunk` rather than read back off the
   * attributes, which no longer have their arrays — see `releaseAfterUpload`.
   * This makes the figure exact rather than estimated, since it is measured
   * before anything can drop it.
   */
  estimateGpuBytes(): number {
    let total = 0;
    for (const bytes of this.geometryBytes.values()) total += bytes;
    return total;
  }

  dispose(): void {
    for (const key of Array.from(this.meshes.keys())) this.removeChunk(key);
    for (const shell of [this.atmosphereMesh, this.skyMesh]) {
      if (!shell) continue;
      shell.geometry.dispose();
      (shell.material as THREE.Material).dispose();
      this.root.remove(shell);
    }
    this.atmosphereMesh = null;
    this.skyMesh = null;
  }
}
